import { Paged } from 'api/common/types/Page';
import Http from 'common/services/Http';
import IdVersion from 'api/common/types/IdVersion';
import { ElementDto, ElementHistoryDto, ElementListDto, ElementUpdatePricesDto } from './Models/ElementDto';
import { ElementsSearchCriteria } from './Models/ElementsSearchCriteria';
import { ElementTypeDto } from './Models/ElementTypeDto';
import CustomFile from 'common/models/CustomFile';

import { PagedSearchCriteria } from 'api/common/types/PagedSearchCriteria';
import { ElementSelectTitleSubTitleOptionDto } from 'common/components/configurableElementsLine/ConfigurableElementsLine';

class ElementsService {
    public getList(criteria: ElementsSearchCriteria) {
        return Http.get<Paged<ElementListDto>>('elements', criteria);
    }

    public getById(id: string, showOnlyFilleParameters?: boolean) {
        return Http.get<ElementDto>('elements/' + id + '/' + showOnlyFilleParameters);
    }

    public getElementTypesCatalog() {
        return Http.get<ElementTypeDto[]>('elements/types/catalog',);
    }

    public create(model: ElementDto, medias: CustomFile[]): Promise<string> {
        return Http.postWithFile('elements', model, medias);
    }

    public update(model: ElementDto, medias: CustomFile[]) {
        return Http.putWithFile('elements', model, medias);
    }

    public remove(model: IdVersion) {
        return Http.put('elements/deactivate', model);
    }

    public getElementsCatalogByFamilyId(criteria: ElementsSearchCriteria) {
        return Http.get<Paged<ElementSelectTitleSubTitleOptionDto>>('elements/elements-by-family', criteria);
    }

    public getElementHistory(elementId: string) {
        return Http.get<ElementHistoryDto[]>('elements/element-history/' + elementId);
    }

    public getAllByCriteria(criteria: Omit<ElementsSearchCriteria, keyof PagedSearchCriteria>) {
        return Http.get<ElementListDto[]>('elements/get-all-by-criteria', criteria);
    }

    public updatePrices(model: ElementUpdatePricesDto) {
        return Http.put('elements/update-prices/', model);
    }

    public startImport(file: File): Promise<ImportJobResponse> {
        const formData = new FormData();
        formData.append('file', file);
        return Http.postFormData<ImportJobResponse>('elements/import-excel', formData);
    }

    public getImportStatus(jobId: string): Promise<ImportStatus> {
        return Http.get<ImportStatus>(`elements/import-status/${jobId}`);
    }

    public startMappedImport(data: ImportElementsMappedRequest): Promise<ImportJobResponse> {
        return Http.post<ImportJobResponse>('elements/import-excel-mapped', data);
    }

    public previewImportPrices(data: ImportPricePreviewRequest): Promise<ImportPricePreviewResultDto[]> {
        return Http.post<ImportPricePreviewResultDto[]>('elements/import-preview-prices', data);
    }

    public getImportSettings(): Promise<ImportSettingsDto> {
        return Http.get<ImportSettingsDto>('elements/import-settings');
    }

    public saveImportSettings(data: SaveImportSettingsDto): Promise<void> {
        return Http.put('elements/import-settings', data);
    }
}

export interface ImportJobResponse {
    jobId: string;
}

export interface ImportStatus {
    jobId: string;
    status: 'processing' | 'completed' | 'failed';
    totalRows: number;
    processedRows: number;
    createdCount: number;
    updatedCount: number;
    suppliersCreated: number;
    errorCount: number;
    errors: string[];
    createdElements: ImportedElementInfo[];
    updatedElements: ImportedElementInfo[];
    startedAt: string;
    completedAt: string | null;
}

export interface ImportedElementInfo {
    id: string;
    name: string;
    familyName: string;
}

export interface ImportElementsMappedRequest {
    rows: ImportElementRowDto[];
}

export interface ImportElementRowDto {
    familyId: string;
    subFamilyId?: string | null;
    elementTypeId?: string | null;
    workTypeId?: string | null;
    unitId?: string | null;
    reference?: string | null;
    name: string;
    fornecedor?: string | null;
    price?: string | null;
    additionalCoefficient?: string | null;
    parameterValues?: Record<string, string>;
    importRowIndex?: number;
    priceClassification?: string | null;
    priceExpressionTemplate?: string | null;
}

export interface ImportPricePreviewRequest {
    columnMappings: { excelColumnIndex: number; elementField: string | null }[];
    rows: ImportPricePreviewRowDto[];
}

export interface ImportPricePreviewRowDto {
    importRowIndex: number;
    sheetRow: number;
    familyId: string;
    subFamilyId?: string | null;
    elementTypeId?: string | null;
    reference?: string | null;
    name?: string | null;
    priceFormula?: string | null;
    priceValue?: number | null;
    parameterValues?: Record<string, string>;
}

export interface ImportPricePreviewResultDto {
    importRowIndex: number;
    classification: 'INHERIT' | 'OWN' | 'FIXED';
    priceExpressionTemplate: string | null;
    displayFormula: string | null;
}

export interface ImportSettingsDto {
    columnMappings?: string; // JSON string of Record<string, string>
}

export interface SaveImportSettingsDto {
    columnMappings: string;
}

export default new ElementsService();
